import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ArrowLeft, Download, CheckCircle, Send, Lock, Upload, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';

export default function ClaimDetailPage() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [claim, setClaim] = useState(null);
  const [participantNames, setParticipantNames] = useState({});
  const [readyConfirm, setReadyConfirm] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reconcileEdits, setReconcileEdits] = useState({});
  const [importResult, setImportResult] = useState(null);

  const fetchData = useCallback(async () => {
    try { const res = await api.get(`/claims/${claimId}`); setClaim(res.data.data); }
    catch (e) { notify.error(e, 'CLAIM_LOAD_FAILED'); navigate('/finance/claims'); }
  }, [claimId, navigate]);
  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get('/participants?pageSize=200')
      .then(res => {
        const map = {};
        (res.data.data || res.data.items || res.data || []).forEach(p => {
          const id = p.id || p._id;
          if (id) map[id] = `${p.first_name || ''} ${p.last_name || ''}`.trim() || (p.name || id);
        });
        setParticipantNames(map);
      })
      .catch(() => {});
  }, []);

  const pName = (id) => participantNames[id] || (id ? `${String(id).slice(0, 8)}…` : 'Unknown');

  const doAction = async (url, body, msg) => {
    setLoading(true);
    try { await api.post(url, body); toast.success(msg); fetchData(); }
    catch (e) { notify.error(e, 'CLAIM_ACTION_FAILED'); }
    finally { setLoading(false); }
  };

  const exportCSV = async () => {
    try {
      const res = await api.get(`/claims/${claimId}/export-csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a');
      a.href = url; a.download = `${claim?.claim_no || 'claim'}.csv`; a.click(); toast.success('CSV exported');
    } catch (e) { notify.error(e, 'CLAIM_EXPORT_FAILED'); }
  };

  const handleReconcile = async () => {
    const updates = Object.entries(reconcileEdits).map(([tsId, edit]) => ({
      timesheet_id: tsId, claim_line_status: edit.status, rejection_reason: edit.reason || undefined,
    })).filter(u => u.claim_line_status);
    if (updates.length === 0) { notify.warn('CLAIM_RECONCILE_EMPTY'); return; }
    doAction(`/claims/${claimId}/reconcile`, { updates }, 'Claim reconciled');
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/claims/${claimId}/template-csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a');
      a.href = url; a.download = `${claim?.claim_no}_recon_template.csv`; a.click(); toast.success('Template downloaded');
    } catch(e) { notify.error(e, 'CLAIM_DOWNLOAD_FAILED'); }
  };

  const importRecon = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try {
      const res = await api.post(`/claims/${claimId}/import-reconciliation`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data.import_result);
      toast.success(`Imported: ${res.data.import_result.rows_applied} rows`);
      fetchData();
    } catch(err) {
      const errData = err.response?.data?.error;
      if (errData?.details?.errors) notify.error(e, 'CLAIM_RECONCILE_IMPORT_FAILED');
      else notify.error(e, 'CLAIM_RECONCILE_IMPORT_FAILED');
    }
    e.target.value = '';
  };

  if (!claim) return <div className="py-12 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="ghost" size="sm" onClick={() => navigate('/finance/claims')} className="-ml-2"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{claim.claim_no}</h1>
          <p className="text-muted-foreground text-sm mt-1">{claim.date_from} to {claim.date_to} | {claim.totals?.count || 0} items | ${(claim.totals?.amount || 0).toFixed(2)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={claim.status} />
          {claim.per_participant_billing_status === 'COMPLETED_WITH_BLOCKS' && (
            <span data-testid="claim-billing-blocks-pill"
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-xs font-medium">
              <AlertTriangle className="h-3 w-3" /> Completed with blocks
            </span>
          )}
          {hasPermission('claims.export') && <Button variant="outline" size="sm" onClick={exportCSV}><Download className="h-3 w-3 mr-1" /> CSV</Button>}
          {hasPermission('claims.export') && claim.status !== 'DRAFT' && <Button variant="outline" size="sm" onClick={downloadTemplate}><FileText className="h-3 w-3 mr-1" /> Recon Template</Button>}
          {hasPermission('claims.reconcile') && ['SUBMITTED','PART_PAID'].includes(claim.status) && (
            <label className="cursor-pointer">
              <Button variant="outline" size="sm" asChild><span><Upload className="h-3 w-3 mr-1" /> Import Recon CSV</span></Button>
              <input type="file" accept=".csv" className="hidden" onChange={importRecon} data-testid="import-recon-input" />
            </label>
          )}
          {claim.status === 'DRAFT' && hasPermission('claims.generate') && <Button size="sm" onClick={() => setReadyConfirm(true)}><CheckCircle className="h-3 w-3 mr-1" /> Mark Ready</Button>}
          {claim.status === 'READY' && hasPermission('claims.submit') && <Button size="sm" onClick={() => setSubmitConfirm(true)}><Send className="h-3 w-3 mr-1" /> Submit</Button>}
        </div>
      </div>

      {/* Per-Participant Billing Blocks (SIL) — a batch with blocked residents
          must never look fully successful. */}
      {(claim.per_participant_billing_blocked || []).length > 0 && (
        <Card className="border-amber-300" data-testid="claim-billing-blocks-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              Billing blocks — {(claim.per_participant_billing_blocked || []).length} participant line(s) not billed
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Participant / Resident</TableHead>
                <TableHead>Shift</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Blocking reason</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(claim.per_participant_billing_blocked || []).map((b, i) => (
                  <TableRow key={i} data-testid={`claim-billing-block-row-${i}`}>
                    <TableCell className="text-sm font-medium">{pName(b.participant_id)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{b.shift_id ? `${String(b.shift_id).slice(0, 8)}…` : '-'}</TableCell>
                    <TableCell className="text-sm">{b.date || '-'}</TableCell>
                    <TableCell><StatusBadge status={b.reason} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Line Items */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Line Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Timesheet</TableHead><TableHead>Category</TableHead><TableHead>Service</TableHead>
              <TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Svc|Trv|N2F|Tpt</TableHead><TableHead>Status</TableHead>
              {claim.status === 'SUBMITTED' && hasPermission('claims.reconcile') && <TableHead>Reconcile</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {(claim.line_items || []).map((li, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-xs">{li.timesheet_id?.slice(0, 8)}...</TableCell>
                  <TableCell><StatusBadge status={li.category} /></TableCell>
                  <TableCell className="text-sm">{li.service_name || 'N/A'}</TableCell>
                  <TableCell className="text-right tabular-nums">{li.hours}h</TableCell>
                  <TableCell className="text-right tabular-nums">${li.rate?.toFixed(2)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">${li.amount?.toFixed(2)}</TableCell>
                  <TableCell className="text-[10px] font-mono text-muted-foreground">{(() => { const b = li.components_breakdown || {}; return `$${(b.service||0).toFixed(0)}|$${(b.travel||0).toFixed(0)}|$${(b.n2f||0).toFixed(0)}|$${(b.transport||0).toFixed(0)}`; })()}</TableCell>
                  <TableCell><StatusBadge status={li.claim_status} /></TableCell>
                  {claim.status === 'SUBMITTED' && hasPermission('claims.reconcile') && (
                    <TableCell>
                      <Select value={reconcileEdits[li.timesheet_id]?.status || ''} onValueChange={(v) => setReconcileEdits({...reconcileEdits, [li.timesheet_id]: {...(reconcileEdits[li.timesheet_id]||{}), status: v}})}>
                        <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent><SelectItem value="PAID">Paid</SelectItem><SelectItem value="REJECTED">Rejected</SelectItem></SelectContent>
                      </Select>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reconcile Button */}
      {claim.status === 'SUBMITTED' && hasPermission('claims.reconcile') && (
        <Button onClick={handleReconcile} disabled={loading} data-testid="reconcile-submit">
          <Lock className="h-4 w-4 mr-2" /> Save Reconciliation
        </Button>
      )}

      {/* Import Result */}
      {(importResult || claim.reconciliation) && (
        <Card>
          <CardContent className="p-4 text-sm">
            <p className="font-medium mb-2">Reconciliation Info</p>
            {claim.reconciliation && (
              <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                <div>Source: {claim.reconciliation.source_file_name || '-'}</div>
                <div>Imported: {claim.reconciliation.imported_at?.replace('T',' ').slice(0,19) || '-'}</div>
                <div>Rows: {claim.reconciliation.rows_processed || '-'}</div>
                <div>Applied: {claim.reconciliation.rows_applied || '-'}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmDialog open={readyConfirm} onOpenChange={setReadyConfirm} title="Mark Ready" description="Mark this claim batch as ready for submission?" confirmLabel="Mark Ready" loading={loading}
        onConfirm={() => { doAction(`/claims/${claimId}/mark-ready`, {}, 'Marked ready'); setReadyConfirm(false); }} />
      <ConfirmDialog open={submitConfirm} onOpenChange={setSubmitConfirm} title="Submit Claim" description="Submit this claim batch? Line items will be marked as SUBMITTED." confirmLabel="Submit" loading={loading}
        onConfirm={() => { doAction(`/claims/${claimId}/submit`, {}, 'Claim submitted'); setSubmitConfirm(false); }} />
    </div>
  );
}
